import { Flex, List, Modal, Text } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useIntl } from "react-intl";

import { NAVIGATION_PATHS } from "../constants";
import {
  FINANCIAL_DOCUMENTS_STEP_ID,
  FINANCIAL_DOCUMENTS_TYPES,
} from "../../hooks/navigation-next/financial-documents";
import { PAGE_TYPE, composePath } from "../../hooks/navigation-next/use-funding-application-navigation-next";

import { MESSAGES, DOCUMENT_NAMES, DOCUMENT_TEMPLATES } from "./message-definitions";

interface IncompleteStep {
  label: string;
  ctaLabel: string;
  navigationPath: string;
  pageType: PAGE_TYPE;
  step?: string;
}

type MissingPlatformType = "revenue" | "accounting" | "marketing";

type ModalVariant = "banking-only" | "platforms-only" | "multi";

function getMissingPlatforms(
  stageCompletionStatus: FundingApplicationSchemas.StageCompletionStatus,
  stagesConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
): MissingPlatformType[] {
  if (!stagesConfig.includePlatformSelection) {
    return [];
  }

  const missing: MissingPlatformType[] = [];

  if (!(stageCompletionStatus.revenue ?? true)) {
    missing.push("revenue");
  }
  if (!(stageCompletionStatus.accounting ?? true)) {
    missing.push("accounting");
  }
  if (!(stageCompletionStatus.marketing ?? true)) {
    missing.push("marketing");
  }

  return missing;
}

function getIncompleteSteps(
  stageCompletionStatus: FundingApplicationSchemas.StageCompletionStatus,
  stagesConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen,
  intl: ReturnType<typeof useIntl>
): IncompleteStep[] {
  const steps: IncompleteStep[] = [];

  if (stagesConfig.banking && !stageCompletionStatus.banking) {
    steps.push({
      label: intl.formatMessage(MESSAGES.addBankingData),
      ctaLabel: intl.formatMessage(MESSAGES.connectBankAccountCta),
      navigationPath: NAVIGATION_PATHS.BANKING,
      pageType: PAGE_TYPE.BANKING,
    });
  }

  const missingPlatforms = getMissingPlatforms(stageCompletionStatus, stagesConfig);

  if (missingPlatforms.length > 0) {
    steps.push({
      label: intl.formatMessage(MESSAGES.connectPlatforms),
      ctaLabel: intl.formatMessage(MESSAGES.connectPlatformsCta),
      navigationPath: NAVIGATION_PATHS.ADD_CONNECTION,
      pageType: PAGE_TYPE.CONNECTORS,
    });
  }

  if (stagesConfig.documents) {
    // Several document types (e.g. pool-bancario + cirbe) can collapse onto one combined step.
    // Group by step first, preserving the position of each step's first configured document, so
    // completing one document in the group doesn't shift the combined step past other steps that
    // were configured after it.
    const stepOrder: string[] = [];
    const stepDocumentTypes = new Map<string, string[]>();
    stagesConfig.documents.forEach((doc) => {
      const step = FINANCIAL_DOCUMENTS_TYPES.includes(doc.documentType)
        ? FINANCIAL_DOCUMENTS_STEP_ID
        : doc.documentType;
      if (!stepDocumentTypes.has(step)) {
        stepOrder.push(step);
        stepDocumentTypes.set(step, []);
      }
      stepDocumentTypes.get(step)?.push(doc.documentType);
    });

    stepOrder.forEach((step) => {
      const documentTypes = stepDocumentTypes.get(step) ?? [];
      const isComplete = documentTypes.every(
        (documentType) => stageCompletionStatus.documents?.[documentType] ?? false
      );
      if (isComplete) {
        return;
      }

      const nameDescriptor = DOCUMENT_NAMES[step as keyof typeof DOCUMENT_NAMES];
      const label = nameDescriptor
        ? intl.formatMessage(DOCUMENT_TEMPLATES.uploadLabel, { documentName: intl.formatMessage(nameDescriptor) })
        : intl.formatMessage(MESSAGES.uploadDocumentCta);

      steps.push({
        label,
        ctaLabel: intl.formatMessage(MESSAGES.uploadDocumentCta),
        navigationPath: composePath(PAGE_TYPE.DOCUMENTS, step),
        pageType: PAGE_TYPE.DOCUMENTS,
        step,
      });
    });
  }

  if (stagesConfig.creditConsent && !stageCompletionStatus.creditConsent) {
    steps.push({
      label: intl.formatMessage(MESSAGES.completeCreditCheck),
      ctaLabel: intl.formatMessage(MESSAGES.completeCreditCheckCta),
      navigationPath: NAVIGATION_PATHS.CREDIT_CHECK_CONSENT,
      pageType: PAGE_TYPE.CREDIT_CHECK_CONSENT,
    });
  }

  return steps;
}

function getModalVariant(steps: IncompleteStep[]): ModalVariant {
  if (steps.length === 1 && steps[0].pageType === PAGE_TYPE.BANKING) {
    return "banking-only";
  }
  if (steps.length === 1 && steps[0].pageType === PAGE_TYPE.CONNECTORS) {
    return "platforms-only";
  }
  return "multi";
}

const PLATFORM_BODY_MESSAGES: Record<MissingPlatformType, (typeof MESSAGES)[keyof typeof MESSAGES]> = {
  revenue: MESSAGES.revenueOnlyBody,
  accounting: MESSAGES.accountingOnlyBody,
  marketing: MESSAGES.marketingOnlyBody,
};

const PLATFORM_LIST_MESSAGES: Record<MissingPlatformType, (typeof MESSAGES)[keyof typeof MESSAGES]> = {
  revenue: MESSAGES.multiplePlatformsRevenue,
  accounting: MESSAGES.multiplePlatformsAccounting,
  marketing: MESSAGES.multiplePlatformsMarketing,
};

function isCurrentPageStep(step: IncompleteStep, currentPage: PAGE_TYPE, currentStep?: string): boolean {
  if (step.pageType !== currentPage) {
    return false;
  }
  if (currentPage === PAGE_TYPE.DOCUMENTS && currentStep && step.step) {
    return step.step === currentStep;
  }
  return true;
}

export interface IncompleteStepsModalProps {
  stageCompletionStatus: FundingApplicationSchemas.StageCompletionStatus;
  stagesConfig: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
  currentPage: PAGE_TYPE;
  currentStep?: string;
  isModalOpen: boolean;
  closeModal: () => void;
  navigate: (path: string) => void;
}

export const IncompleteStepsModal = ({
  stageCompletionStatus,
  stagesConfig,
  currentPage,
  currentStep,
  isModalOpen,
  closeModal,
  navigate,
}: IncompleteStepsModalProps) => {
  const intl = useIntl();

  const allIncompleteSteps = getIncompleteSteps(stageCompletionStatus, stagesConfig, intl);

  if (allIncompleteSteps.length === 0) {
    return null;
  }

  const variant = getModalVariant(allIncompleteSteps);

  const handleStepAction = (step: IncompleteStep) => {
    closeModal();
    if (!isCurrentPageStep(step, currentPage, currentStep)) {
      navigate(step.navigationPath);
    }
  };

  if (variant === "banking-only") {
    return (
      <Modal
        title={intl.formatMessage(MESSAGES.bankingOnlyTitle)}
        isOpen={isModalOpen}
        onClose={closeModal}
        closeOnClickElseWhere={false}
        primaryAction={{
          content: intl.formatMessage(MESSAGES.bankingOnlyCta),
          onAction: () => handleStepAction(allIncompleteSteps[0]),
        }}
      >
        <Text>{intl.formatMessage(MESSAGES.bankingOnlyBody)}</Text>
      </Modal>
    );
  }

  if (variant === "platforms-only") {
    const missingPlatforms = getMissingPlatforms(stageCompletionStatus, stagesConfig);

    return (
      <Modal
        title={intl.formatMessage(MESSAGES.platformsOnlyTitle)}
        isOpen={isModalOpen}
        onClose={closeModal}
        closeOnClickElseWhere={false}
        primaryAction={{
          content: intl.formatMessage(MESSAGES.platformsOnlyCta),
          onAction: () => handleStepAction(allIncompleteSteps[0]),
        }}
      >
        {missingPlatforms.length === 1 ? (
          <Text>{intl.formatMessage(PLATFORM_BODY_MESSAGES[missingPlatforms[0]])}</Text>
        ) : (
          <Flex direction="column" gap="4">
            <Text>{intl.formatMessage(MESSAGES.multiplePlatformsBody)}</Text>
            <List type="bullet">
              {missingPlatforms.map((platform) => (
                <List.Item key={platform}>{intl.formatMessage(PLATFORM_LIST_MESSAGES[platform])}</List.Item>
              ))}
            </List>
          </Flex>
        )}
      </Modal>
    );
  }

  const primaryStep = allIncompleteSteps[0];
  const secondaryStep = allIncompleteSteps.length >= 2 ? allIncompleteSteps[1] : undefined;

  return (
    <Modal
      title={intl.formatMessage(MESSAGES.multiTitle)}
      isOpen={isModalOpen}
      onClose={closeModal}
      closeOnClickElseWhere={false}
      primaryAction={{
        content: primaryStep.ctaLabel,
        onAction: () => handleStepAction(primaryStep),
      }}
      secondaryAction={
        secondaryStep
          ? {
              content: secondaryStep.ctaLabel,
              onAction: () => handleStepAction(secondaryStep),
            }
          : undefined
      }
    >
      <Flex direction="column" gap="4">
        <Text>{intl.formatMessage(MESSAGES.multiBody)}</Text>
        <List type="bullet">
          {allIncompleteSteps.map((step) => (
            <List.Item key={step.navigationPath}>{step.label}</List.Item>
          ))}
        </List>
      </Flex>
    </Modal>
  );
};
